import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
    useState,
    useMemo,
    useRef,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
} from 'react';
import CaretDownIcon from '../CustomIcons/CaretDownIcon';
import CaretUpIcon from '../CustomIcons/CaretUpIcon';
import InfoIcon from '../CustomIcons/InfoIcon';
import WarningTriangleIcon from '../CustomIcons/WarningTriangleIcon';
import Tooltip from '../Tooltip';
import TruncatedTooltip from '../TruncatedTooltip';
import { FOCUS_RING_INSET } from '../focusRing';
import { activateOnEnterOrSpace, isFromInteractiveDescendant } from '../keyboardActivation';
import {
    FOREGROUND_RESET,
    SELECTED_ROW_ACCENT,
    SELECTED_ROW_BACKGROUND,
    SELECTED_ROW_FOREGROUND,
    SELECTED_TEXT_CONTEXT,
} from '../selectedRow';
import TablePagination from './TablePagination';
import {
    TableColumnDefinition,
    TableColumns,
    TablePaginationPosition,
    TableRowActions,
    TableRowSelection,
    TableSize,
    TableType,
    TableVisibleOrderHandler,
} from './types';

export type { TableRowActions };

export type RowVariant = 'success' | 'warning' | 'error' | 'info';

export type TableHeaderVariant = 'inline' | 'detached';

export type TableProps<T = Record<string, unknown>> = {
    columns: TableColumns<T>;
    data: T[];
    loading?: boolean;
    error?: boolean;
    errorTitle?: string;
    errorSubTitle?: string;
    emptyTitle?: string;
    emptySubTitle?: string;
    bordered?: boolean;
    showHeader?: boolean;
    rowKey?: string | ((record: T) => string);
    scrollHeight?: string | number;
    type?: TableType;
    size?: TableSize;
    /** Vertical density of body rows. 'compact' halves the cell padding so
     *  rows carrying controls (buttons, dropdowns) sit close to text-row
     *  height; headers keep their own padding. */
    rowDensity?: 'default' | 'compact';
    layout?: 'fixed' | 'auto';
    headerVariant?: TableHeaderVariant;
    rowClassName?: (record: T, index: number) => string;
    rowVariant?: (record: T, index: number) => RowVariant | undefined;
    onRow?: (record: T, index: number) => TableRowActions<T>;
    onClickRow?: (record: T, index: number) => void;
    /** Row the master-detail view is showing, resolved against `rowKey`. Controlled, so
     *  selection can change without a row click and the highlight follows. Unlike
     *  `rowSelection` it paints no control — a tint, an accent bar and `aria-current`.
     *  Follows its record through a sort only with a resolvable `rowKey`. */
    selectedRowKey?: React.Key;
    /** Whether a selection change scrolls the row into view. Default true.
     *
     *  Set `false` when the initial key comes from a URL: the reveal uses `scrollIntoView`,
     *  which walks every scrollable ancestor, so a deep-linked row below the fold jumps the
     *  page on load. Selection still highlights; only the scroll is suppressed. */
    scrollSelectedIntoView?: boolean;
    rowSelection?: TableRowSelection<T>;
    pagination?: {
        current?: number;
        pageSize?: number;
        total?: number;
        showSizeChanger?: boolean;
        showQuickJumper?: boolean;
        showTotal?: (total: number, range: [number, number]) => React.ReactNode;
        pageSizeOptions?: string[];
        position?: TablePaginationPosition[];
        onChange?: (page: number, pageSize: number) => void;
        onShowSizeChange?: (current: number, size: number) => void;
    };
    className?: string;
    style?: React.CSSProperties;
    /** When set, makes the detached-variant header sticky at this offset (px) from the
     *  top of the nearest scrolling ancestor. Use this when the table sits inside a
     *  page-level scroll container with other sticky elements (e.g. tabs + toolbar)
     *  stacked above. */
    stickyHeaderTop?: number;
    scrollX?: boolean;
    rowSection?: (record: T) => string;
    renderSectionDivider?: (sectionKey: string, records: T[]) => React.ReactNode;
    /** Reports the rows in render order — sorted and section-grouped, so not `data` order,
     *  and always all of `data`. An equal-but-new array does not re-fire, so a host cannot loop.
     *  Needs a `rowKey`, or records that keep their object identity across renders. */
    onVisibleOrderChange?: TableVisibleOrderHandler<T>;
    /** Windowed rendering for large datasets (detached variant only), powered by
     *  TanStack Virtual: only rows near the viewport stay mounted; spacers keep the
     *  scrollbar reflecting the full list. The table body becomes the scroll
     *  container, so give it a bounded height via `scrollHeight`. Row heights are
     *  measured per row (non-uniform rows are fine). */
    virtual?: boolean;
    /** Estimated row height (px) used by `virtual` before rows are measured. */
    virtualRowHeightEstimate?: number;
    /** With `virtual`: virtualize against this ancestor scroll container instead of
     *  the table body (e.g. a page-level scroller so content above the table can
     *  scroll away). Pass the element from state (not a ref) so it arrives after
     *  mount. The table body stops scrolling when this is set. */
    virtualScrollElement?: HTMLElement | null;
};

const DEFAULT_SCROLL_COL_WIDTH = 160;

const sameRowOrder = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((key, index) => key === b[index]);

// Kept per object, never derived from order: any identity read off the current order is the
// same sequence after a reorder, so the reorder would be suppressed.
const KEYLESS_ID_PREFIX = '\u0000';

type KeylessIds = { ids: WeakMap<object, string>; next: number };

const keylessId = (record: unknown, registry: KeylessIds, index: number): string => {
    if (typeof record !== 'object' || record === null) {
        return `${KEYLESS_ID_PREFIX}i${index}`;
    }
    const seen = registry.ids.get(record);
    if (seen !== undefined) {
        return seen;
    }
    const id = `${KEYLESS_ID_PREFIX}${registry.next++}`;
    registry.ids.set(record, id);
    return id;
};

const visibleRowOrder = <T,>(
    sortedData: readonly T[],
    rowKey: string | ((record: T) => string),
    registry: KeylessIds,
): string[] => {
    if (typeof rowKey === 'function') {
        return sortedData.map((record) => rowKey(record));
    }
    return sortedData.map((record, index) => {
        const key = (record as Record<string, unknown>)[rowKey];
        return key === undefined || key === null ? keylessId(record, registry, index) : String(key);
    });
};

const fixedPxWidth = (width: string | number | undefined): number | undefined => {
    if (typeof width === 'number') {
        return width;
    }
    if (typeof width === 'string') {
        const match = width.trim().match(/^(\d+(?:\.\d+)?)px$/);
        if (match) {
            return Number(match[1]);
        }
    }
    return undefined;
};

const computeScrollMinWidth = (columns: TableColumns<unknown>): number =>
    columns.reduce((sum, column) => {
        const fixed = fixedPxWidth(column.width);
        if (fixed !== undefined) {
            return sum + fixed;
        }
        if (typeof column.minWidth === 'number') {
            return sum + column.minWidth;
        }
        return sum + DEFAULT_SCROLL_COL_WIDTH;
    }, 0);

const isString = (value: unknown): value is string => typeof value === 'string';

const getSizeClasses = (size: TableSize): string => {
    const sizeMap: Record<TableSize, string> = {
        small: 'text-[length:var(--malachite-font-size-small)]',
        medium: 'text-[length:var(--malachite-font-size-medium)]',
        large: 'text-[length:var(--malachite-font-size-large)]',
    };
    return sizeMap[size];
};

const ROW_BORDER_STYLE: React.CSSProperties = {
    borderBottom: '1px solid var(--malachite-card-border, #e8e8e8)',
};

const syncScroll = (source: HTMLDivElement | null, target: HTMLDivElement | null): void => {
    if (source && target && target.scrollLeft !== source.scrollLeft) {
        target.scrollLeft = source.scrollLeft;
    }
};

const PAGINATION_RESERVED_HEIGHT = 56;

const DEFAULT_VIRTUAL_ROW_HEIGHT = 44;
const VIRTUAL_OVERSCAN_ROWS = 8;
// Bounded so a row that never mounts can't spin forever.
const VIRTUAL_SCROLL_CORRECTION_FRAMES = 10;

// Section cards are clipped to this radius. The first and last row echo it so the selected
// row's accent bar curves into the corner instead of being cut off by the clip.
const SECTION_CARD_RADIUS = 8;
/** The detached table's outer card: `rounded-xl` + `p-[4px]` on the container below. Read from
 *  the token the container itself rounds by, or overriding `radius.xl` pinches the corners. */
const DETACHED_CARD_RADIUS = 'var(--malachite-radius-xl, 12px)';
const DETACHED_CARD_PADDING = 4;

/** The colour behind the table's own card, painted into the corner notches. A context channel,
 *  not a theme token: no palette value, not settable through `applyTheme`, absent from
 *  `getCurrentTheme`. Set it on an ancestor when a docked table does not sit on a card. */
const SURROUND_CHANNEL = '--malachite-ctx-table-surround';

/** Cuts the docked header's top corners. A plain `border-radius` is invisible here: the
 *  container behind paints the same grey, so the notch would just reveal grey. */
const StickyHeaderCornerCap: React.FC = () => {
    const r = DETACHED_CARD_RADIUS;
    const card = `var(${SURROUND_CHANNEL}, var(--malachite-card-background, #ffffff))`;

    return (
        <div
            aria-hidden="true"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: r,
                pointerEvents: 'none',
                background: [
                    `radial-gradient(circle ${r} at ${r} ${r}, transparent calc(${r} - 0.5px), ${card} ${r}) top left / ${r} ${r} no-repeat`,
                    `radial-gradient(circle ${r} at 0 ${r}, transparent calc(${r} - 0.5px), ${card} ${r}) top right / ${r} ${r} no-repeat`,
                ].join(', '),
            }}
        />
    );
};

type VirtualMeasureProps = {
    measureRef: (element: HTMLDivElement | null) => void;
    index: number;
};

/** Lets the table scroll to a row a card has not mounted. The virtualizer is per-card, so
 *  `startIndex`/`count` map a global index to the owning card. */
type VirtualCardHandle = {
    startIndex: number;
    count: number;
    scrollToIndex: (localIndex: number) => void;
};

type VirtualSectionCardProps<T> = {
    records: T[];
    startIndex: number;
    scrollElement: HTMLElement | null;
    estimateRowHeight: number;
    style: React.CSSProperties;
    renderRow: (
        record: T,
        index: number,
        isLastInSection: boolean,
        isFirstInSection: boolean,
        virtualMeasure?: VirtualMeasureProps,
        tabStopIndex?: number,
    ) => React.ReactElement;
    /** Called with null on unmount. */
    registerHandle?: (cardKey: string, handle: VirtualCardHandle | null) => void;
    cardKey: string;
    /** The table's remembered roving-tabindex position, in global row indices. */
    rovingIndex: number;
};

/** One section card rendered as a TanStack Virtual window over its rows. */
const VirtualSectionCard = <T,>({
    records,
    startIndex,
    scrollElement,
    estimateRowHeight,
    style,
    renderRow,
    registerHandle,
    cardKey,
    rovingIndex,
}: VirtualSectionCardProps<T>): React.ReactElement => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies(records.length): the card's offset must be re-measured when section sizes change — content above this card moves
    // biome-ignore lint/correctness/useExhaustiveDependencies(startIndex): a changed start index means the sections above grew or shrank, which moves this card
    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card || !scrollElement) {
            return;
        }
        const measure = (): void => {
            const margin =
                card.getBoundingClientRect().top -
                scrollElement.getBoundingClientRect().top +
                scrollElement.scrollTop;
            setScrollMargin((previous) => (Math.abs(previous - margin) < 1 ? previous : margin));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        // A one-shot measure goes stale: sections above attach their virtualizers in a later
        // commit and this card does not re-render when they grow. Watch every preceding sibling
        // up to the scroll container, crossing shadow boundaries.
        const observer = new ResizeObserver(measure);
        let node: Element | null = card;
        while (node && node !== scrollElement) {
            let sibling = node.previousElementSibling;
            while (sibling) {
                observer.observe(sibling);
                sibling = sibling.previousElementSibling;
            }
            const parent: Element | null = node.parentElement;
            if (parent) {
                node = parent;
            } else {
                const root = node.getRootNode();
                node = root instanceof ShadowRoot ? root.host : null;
            }
        }
        return () => observer.disconnect();
    }, [scrollElement, records.length, startIndex]);

    const virtualizer = useVirtualizer({
        count: records.length,
        // State, not a ref: an ancestor's ref is not attached when this child's layout effects
        // run, so a ref would leave the virtualizer without a scroll element.
        getScrollElement: () => scrollElement,
        estimateSize: () => estimateRowHeight,
        overscan: VIRTUAL_OVERSCAN_ROWS,
        scrollMargin,
    });

    useEffect(() => {
        if (!registerHandle) {
            return;
        }
        registerHandle(cardKey, {
            startIndex,
            count: records.length,
            scrollToIndex: (localIndex) =>
                virtualizer.scrollToIndex(localIndex, { align: 'center' }),
        });
        return () => registerHandle(cardKey, null);
    }, [registerHandle, cardKey, startIndex, records.length, virtualizer]);

    const items = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    // While the remembered row is outside the window every mounted row would be tabIndex -1 and
    // the table would leave the page tab order. Stand in with the nearest mounted row.
    const ownsRovingIndex = rovingIndex >= startIndex && rovingIndex < startIndex + records.length;
    const tabStopIndex =
        ownsRovingIndex && items.length > 0
            ? Math.min(
                  Math.max(rovingIndex, startIndex + items[0].index),
                  startIndex + items[items.length - 1].index,
              )
            : rovingIndex;
    const paddingTop = items.length > 0 ? items[0].start - scrollMargin : 0;
    const paddingBottom =
        items.length > 0 ? totalSize - (items[items.length - 1].end - scrollMargin) : 0;

    return (
        <div ref={cardRef} className="flex flex-col w-full" style={style}>
            {paddingTop > 0 && (
                <div aria-hidden="true" style={{ height: paddingTop, flexShrink: 0 }} />
            )}
            {items.map((item) =>
                renderRow(
                    records[item.index],
                    startIndex + item.index,
                    item.index === records.length - 1,
                    item.index === 0,
                    { measureRef: virtualizer.measureElement, index: item.index },
                    tabStopIndex,
                ),
            )}
            {paddingBottom > 0 && (
                <div aria-hidden="true" style={{ height: paddingBottom, flexShrink: 0 }} />
            )}
        </div>
    );
};

const computeScrollableMaxHeight = (
    scrollHeight: string | number,
    reservePagination: boolean,
): string => {
    const reserved = reservePagination ? PAGINATION_RESERVED_HEIGHT : 0;
    if (typeof scrollHeight === 'number') {
        return `${Math.max(0, scrollHeight - reserved)}px`;
    }
    return `calc(${scrollHeight} - ${reserved}px)`;
};

const cellWidthStyle = (column: TableColumnDefinition<unknown>): React.CSSProperties => {
    const width = column.width;
    if (width === undefined || width === null) {
        return {};
    }
    const value = typeof width === 'number' ? `${width}px` : width;
    // flexBasis keeps width columns the same size in header and body so they align.
    return { flexBasis: value, maxWidth: value };
};

const fixedSlotStyle = (column: TableColumnDefinition<unknown>): React.CSSProperties => {
    const width = column.width;
    if (width === undefined || width === null) {
        return {};
    }
    const value = typeof width === 'number' ? `${width}px` : width;
    return { width: value, flexBasis: value };
};

const ariaSortValue = (
    sortDirection: 'asc' | 'desc' | null | undefined,
): 'ascending' | 'descending' | 'none' => {
    if (sortDirection === 'asc') {
        return 'ascending';
    }
    if (sortDirection === 'desc') {
        return 'descending';
    }
    return 'none';
};

type ColumnAlign = TableColumnDefinition<unknown>['align'];

/** A header's content is a flex row, so only `justify-*` moves it — `text-align` cannot. */
const alignJustifyClass = (align: ColumnAlign): string =>
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

/**
 * Detached only. Left keeps `justify-between` because that is where the detached variant already
 * draws its caret, and moving it would restyle tables that set no `align`.
 */
const sortableHeaderJustifyClass = (align: ColumnAlign): string =>
    align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-between';

const alignTextClass = (align: ColumnAlign): string =>
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

/** A titleless fixed-width slot is the row-actions column, so an absent `align` stays trailing. */
const fixedSlotJustifyClass = (align: ColumnAlign): string =>
    align ? alignJustifyClass(align) : 'justify-end';

const HeaderInfo = ({ info }: { info: string }): React.ReactElement => (
    <Tooltip text={info} placement="top">
        <InfoIcon
            size={13}
            className="shrink-0 text-[var(--malachite-text-secondary,#bbb)]"
        />
    </Tooltip>
);

const sortButtonProps = (
    title: React.ReactNode,
    onActivate?: () => void,
): React.ButtonHTMLAttributes<HTMLButtonElement> => ({
    type: 'button',
    onClick: onActivate,
    'aria-label': typeof title === 'string' ? `Sort by ${title}` : 'Sort column',
});

type TableCellProps<T> = {
    column: TableColumnDefinition<T>;
    record: T;
    index: number;
    size: TableSize;
    paddingYClass: string;
    bordered: boolean;
    isFirstRow: boolean;
    isLastRow: boolean;
    isFirstColumn: boolean;
    isLastColumn: boolean;
    variantBackground: string | undefined;
    /** First column only: a box-shadow on the `<tr>` is unreliable under `border-collapse`. */
    showSelectedAccent?: boolean;
    /** Set on every cell of a selected row — in a `<table>` cells paint over the `<tr>`. */
    isSelected?: boolean;
};

const TableCell = <T,>({
    column,
    record,
    index,
    size,
    paddingYClass,
    bordered,
    isFirstRow,
    isLastRow,
    isFirstColumn,
    isLastColumn,
    variantBackground,
    showSelectedAccent,
    isSelected,
}: TableCellProps<T>): React.ReactElement => {
    const rawValue = (record as Record<string, unknown>)[column.dataIndex as string];
    const cellContent = column.render
        ? column.render(rawValue, record, index)
        : rawValue == null
          ? ''
          : String(rawValue);
    const titleText = column.ellipsis && isString(rawValue) ? rawValue : undefined;
    return (
        <td
            className={`
                table-cell px-4 ${paddingYClass} ${getSizeClasses(size)}
                ${column.ellipsis ? 'truncate overflow-hidden whitespace-nowrap' : ''}
                ${bordered ? 'table-cell-bordered' : ''}
                ${bordered && isFirstRow ? 'table-cell-bordered-top' : ''}
                ${bordered && isFirstColumn ? 'table-cell-bordered-left' : ''}
                ${bordered && isLastColumn ? 'table-cell-bordered-right' : ''}
                ${bordered && isFirstColumn && isFirstRow ? 'rounded-tl-lg' : ''}
                ${bordered && isLastColumn && isFirstRow ? 'rounded-tr-lg' : ''}
                ${bordered && isFirstColumn && isLastRow ? 'rounded-bl-lg' : ''}
                ${bordered && isLastColumn && isLastRow ? 'rounded-br-lg' : ''}
                ${alignTextClass(column.align)}
            `}
            style={{
                width: column.width,
                backgroundColor:
                    variantBackground ??
                    // Mirror the row's background, or the tint composites over the wrong opaque
                    // base — they differ in dark mode.
                    (isSelected
                        ? bordered
                            ? 'var(--malachite-table-borderedRow-background, #fff)'
                            : 'var(--malachite-table-row-background, #fff)'
                        : undefined),
                // Its own layer, so the tint composites over the cell instead of replacing it.
                backgroundImage: isSelected
                    ? `linear-gradient(${SELECTED_ROW_BACKGROUND}, ${SELECTED_ROW_BACKGROUND})`
                    : undefined,
                // Content pinning its own colour follows via the channel below.
                color: isSelected
                    ? SELECTED_ROW_FOREGROUND
                    : 'var(--malachite-table-row-foreground)',
                ...(isSelected ? SELECTED_TEXT_CONTEXT : {}),
                // Inset box-shadow, not `ring-*`: --tw-ring-* silently doesn't paint in a shadow root.
                boxShadow: showSelectedAccent ? `inset 2px 0 0 ${SELECTED_ROW_ACCENT}` : undefined,
                borderBottom: bordered
                    ? undefined
                    : isLastRow
                      ? 'none'
                      : '1px solid var(--malachite-table-row-border)',
            }}
            title={titleText}
        >
            {cellContent}
        </td>
    );
};

type DetachedHeaderCellProps<T> = {
    column: TableColumnDefinition<T>;
    sortDirection: 'asc' | 'desc' | null;
    onSort?: () => void;
};

const DetachedHeaderCell = <T,>({
    column,
    sortDirection,
    onSort,
}: DetachedHeaderCellProps<T>): React.ReactElement => {
    const isSortable = Boolean(column.sorter);
    const titleIsEmpty = column.title === '' || column.title == null;
    const hasFixedWidth = titleIsEmpty && column.width !== undefined;

    if (hasFixedWidth) {
        return (
            <div
                className="h-full shrink-0"
                style={fixedSlotStyle(column as TableColumnDefinition<unknown>)}
            />
        );
    }

    if (titleIsEmpty) {
        return (
            <div
                className="flex flex-[1_0_0] h-full min-w-0"
                style={cellWidthStyle(column as TableColumnDefinition<unknown>)}
            />
        );
    }

    const titleNode =
        typeof column.title === 'string' ? (
            <p className="font-medium text-[13px] text-[#0a0a0a] tracking-[0.13px] whitespace-nowrap truncate">
                {column.title}
            </p>
        ) : (
            column.title
        );

    const sortCaret = (
        <CaretDownIcon
            size={13}
            className={`shrink-0 transition-transform ${
                sortDirection ? 'text-[#0a0a0a]' : 'text-[#bbb]'
            } ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
        />
    );

    if (!isSortable) {
        return (
            <div
                className={`flex flex-[1_0_0] items-center ${alignJustifyClass(column.align)} min-w-0 overflow-hidden px-4 py-3`}
                style={cellWidthStyle(column as TableColumnDefinition<unknown>)}
            >
                <div className="flex items-center gap-1 min-w-0">
                    {titleNode}
                    {column.info && <HeaderInfo info={column.info} />}
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex flex-[1_0_0] min-w-0 overflow-hidden"
            style={cellWidthStyle(column as TableColumnDefinition<unknown>)}
        >
            <button
                {...sortButtonProps(column.title, onSort)}
                className={`flex flex-1 items-center ${sortableHeaderJustifyClass(column.align)} gap-1 min-w-0 px-4 py-3 cursor-pointer appearance-none bg-transparent border-0 ${alignTextClass(column.align)}`}
            >
                <div className="flex items-center gap-1 min-w-0">
                    {titleNode}
                    {column.info && <HeaderInfo info={column.info} />}
                </div>
                {sortCaret}
            </button>
        </div>
    );
};

type DetachedCellProps<T> = {
    column: TableColumnDefinition<T>;
    record: T;
    index: number;
    size: TableSize;
    paddingYClass: string;
    isSelected?: boolean;
};

const DetachedCell = <T,>({
    column,
    record,
    index,
    size,
    paddingYClass,
    isSelected = false,
}: DetachedCellProps<T>): React.ReactElement => {
    const rawValue = (record as Record<string, unknown>)[column.dataIndex as string];
    const cellContent = column.render
        ? column.render(rawValue, record, index)
        : rawValue == null
          ? ''
          : String(rawValue);
    const titleIsEmpty = column.title === '' || column.title == null;
    const hasFixedWidth = titleIsEmpty && column.width !== undefined;
    const isStringValue = isString(rawValue) && rawValue.length > 0;

    // Text channel — see the classic cell.
    const selectionStyle: React.CSSProperties = isSelected
        ? {
              color: SELECTED_ROW_FOREGROUND,
              ...SELECTED_TEXT_CONTEXT,
          }
        : {};

    if (hasFixedWidth) {
        return (
            <div
                className={`flex items-center ${fixedSlotJustifyClass(column.align)} shrink-0 overflow-hidden px-4 ${paddingYClass}`}
                style={{
                    ...fixedSlotStyle(column as TableColumnDefinition<unknown>),
                    ...selectionStyle,
                }}
            >
                {cellContent}
            </div>
        );
    }

    // Strings only: wrapping arbitrary JSX in a truncating span breaks layout.
    const cellBody =
        !column.render && isStringValue ? (
            <TruncatedTooltip text={rawValue}>{cellContent}</TruncatedTooltip>
        ) : (
            cellContent
        );

    return (
        <div
            className={`flex flex-[1_0_0] items-center min-w-0 overflow-hidden px-4 ${paddingYClass} ${getSizeClasses(size)} ${alignJustifyClass(column.align)}`}
            style={{
                ...cellWidthStyle(column as TableColumnDefinition<unknown>),
                ...selectionStyle,
            }}
        >
            {cellBody}
        </div>
    );
};

const Table = <T = Record<string, unknown>>({
    columns,
    data,
    loading = false,
    error = false,
    errorTitle = 'Data unavailable',
    errorSubTitle = 'There was an issue retrieving the data. Please try again.',
    emptyTitle = 'No data',
    emptySubTitle = '',
    bordered = false,
    showHeader = true,
    rowKey = 'key',
    scrollHeight,
    size = 'small',
    rowDensity = 'default',
    layout = 'auto',
    headerVariant = 'inline',
    rowClassName,
    rowVariant,
    onRow,
    onClickRow,
    selectedRowKey,
    scrollSelectedIntoView = true,
    rowSelection,
    pagination,
    className = '',
    style,
    stickyHeaderTop,
    scrollX = false,
    rowSection,
    renderSectionDivider,
    onVisibleOrderChange,
    virtual = false,
    virtualRowHeightEstimate,
    virtualScrollElement = null,
}: TableProps<T>) => {
    const bodyCellPaddingY = rowDensity === 'compact' ? 'py-1.5' : 'py-3';
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);

    const headerScrollRef = useRef<HTMLDivElement>(null);
    const bodyScrollRef = useRef<HTMLDivElement | null>(null);
    // Scoped lookups: querying the table's own root keeps sibling tables out.
    const containerRef = useRef<HTMLDivElement | null>(null);
    // A ref, not state: state here would re-render the whole table for no visual reason.
    const virtualCardsRef = useRef<Map<string, VirtualCardHandle>>(new Map());
    const registerVirtualCard = useCallback(
        (cardKey: string, handle: VirtualCardHandle | null): void => {
            if (handle) {
                virtualCardsRef.current.set(cardKey, handle);
            } else {
                virtualCardsRef.current.delete(cardKey);
            }
        },
        [],
    );

    // Roving tabindex, held in state so the position survives the virtualiser unmounting the
    // focused row.
    const tableId = useId();
    const [focusedRowIndex, setFocusedRowIndex] = useState(0);

    const isVirtual = virtual && headerVariant === 'detached';
    const virtualEstimate = virtualRowHeightEstimate ?? DEFAULT_VIRTUAL_ROW_HEIGHT;
    // State, so virtual cards re-render and attach their virtualizers once it exists.
    const [bodyScrollElement, setBodyScrollElement] = useState<HTMLDivElement | null>(null);
    // Stable identity so React doesn't detach/reattach every render.
    const bodyScrollRefCallback = useCallback((element: HTMLDivElement | null) => {
        bodyScrollRef.current = element;
        if (element) {
            setBodyScrollElement((previous) => (previous === element ? previous : element));
        }
    }, []);
    const virtualScroller = isVirtual ? (virtualScrollElement ?? bodyScrollElement) : null;
    // With an external scroller the body must not compete for scrolling.
    const bodyOwnsScroll = !(isVirtual && virtualScrollElement);

    const isStickyHeader =
        headerVariant === 'detached' &&
        stickyHeaderTop !== undefined &&
        showHeader &&
        !scrollHeight &&
        (!isVirtual || !bodyOwnsScroll);

    // The header height exists only to offset the selection reveal, so a table that never
    // selects must not pay for the observer or its state updates (ENG-1166, ENG-1353).
    const usesSelection = selectedRowKey !== undefined && selectedRowKey !== null;

    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    useEffect(() => {
        const header = headerScrollRef.current;
        if (!header || !isStickyHeader || !usesSelection || typeof ResizeObserver === 'undefined') {
            setStickyHeaderHeight(0);
            return;
        }
        // Border box: the header occludes rows with its padding too. Re-reading the element
        // would force the layout this observer avoids (ENG-1166); jsdom has no `borderBoxSize`.
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            const height = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? 0;
            setStickyHeaderHeight((previous) =>
                Math.abs(previous - height) < 1 ? previous : height,
            );
        });
        observer.observe(header);
        return () => observer.disconnect();
    }, [isStickyHeader, usesSelection]);

    // scrollIntoView ignores sticky overlays and parks rows under them; reserve the occluded
    // strip. `stickyHeaderTop` covers what the consumer stacks above, this adds the header.
    const rowScrollMarginTop = isStickyHeader
        ? (stickyHeaderTop ?? 0) + stickyHeaderHeight
        : undefined;

    const { sortedData, sectionDividers } = useMemo(() => {
        // dataIndex, not the render-only key.
        const sortRecords = (records: T[]): T[] => {
            if (!sortConfig) return records;

            const sortColumn = columns.find((col) => col.key === sortConfig.key);
            if (!sortColumn) return records;

            if (typeof sortColumn.sorter === 'function') {
                const sorted = [...records].sort(sortColumn.sorter);
                return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
            }

            const field = (sortColumn.dataIndex as string) ?? sortConfig.key;
            return [...records].sort((a, b) => {
                const aComparable = (a as Record<string, unknown>)[field] as string | number;
                const bComparable = (b as Record<string, unknown>)[field] as string | number;

                if (aComparable < bComparable) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aComparable > bComparable) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        };

        if (!rowSection) {
            return {
                sortedData: sortRecords(data),
                sectionDividers: new Map<number, { key: string; records: T[] }>(),
            };
        }

        const groups: { key: string; records: T[] }[] = [];
        const groupIndexByKey = new Map<string, number>();
        for (const record of data) {
            const sectionKey = rowSection(record);
            let groupIndex = groupIndexByKey.get(sectionKey);
            if (groupIndex === undefined) {
                groupIndex = groups.length;
                groupIndexByKey.set(sectionKey, groupIndex);
                groups.push({ key: sectionKey, records: [] });
            }
            groups[groupIndex].records.push(record);
        }

        const rows: T[] = [];
        const dividers = new Map<number, { key: string; records: T[] }>();
        for (const group of groups) {
            const sectionRows = sortRecords(group.records);
            dividers.set(rows.length, { key: group.key, records: sectionRows });
            rows.push(...sectionRows);
        }
        return { sortedData: rows, sectionDividers: dividers };
    }, [data, sortConfig, columns, rowSection]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return prev.direction === 'asc' ? { key, direction: 'desc' } : null;
            }
            return { key, direction: 'asc' };
        });
    };

    // Not a useCallback reading a ref: writing the prop into a ref during render makes render
    // impure, and React may discard render work.
    const getRowKey = (record: T, index: number): string => {
        if (typeof rowKey === 'function') {
            return rowKey(record);
        }
        // ?? not ||: a falsy-but-real key (id 0, '') must keep its identity.
        return String((record as Record<string, unknown>)[rowKey] ?? index);
    };
    // Stands in for `getRowKey` below: an inline `rowKey={(r) => r.id}` changes identity every
    // render, costing a findIndex over the dataset each time. A function rowKey is taken as pure.
    const rowKeyDep = typeof rowKey === 'function' ? null : rowKey;

    // By key, not identity: an inline `data={rows.filter(…)}` would report every render.
    const reportedRowOrderRef = useRef<string[] | undefined>(undefined);
    const keylessIdsRef = useRef<KeylessIds | undefined>(undefined);
    // biome-ignore lint/correctness/useExhaustiveDependencies: rowKeyDep deliberately stands in for rowKey — see the note on rowKeyDep above
    useEffect(() => {
        if (!onVisibleOrderChange) {
            return;
        }
        keylessIdsRef.current ??= { ids: new WeakMap(), next: 0 };
        const rowOrder = visibleRowOrder(sortedData, rowKey, keylessIdsRef.current);
        const reported = reportedRowOrderRef.current;
        if (reported && sameRowOrder(reported, rowOrder)) {
            return;
        }
        reportedRowOrderRef.current = rowOrder;
        onVisibleOrderChange(sortedData);
    }, [sortedData, rowKeyDep, onVisibleOrderChange]);

    // Clamped so a shrinking dataset can't leave every row untabbable.
    const rovingIndex = Math.min(focusedRowIndex, Math.max(0, sortedData.length - 1));

    // Sorted-order position; with no resolvable `rowKey`, the `onClickRow` index.
    // biome-ignore lint/correctness/useExhaustiveDependencies: rowKeyDep deliberately stands in for getRowKey — see the note on rowKeyDep above
    const selectedIndex = useMemo(() => {
        if (selectedRowKey === undefined || selectedRowKey === null) {
            return -1;
        }
        const target = String(selectedRowKey);
        return sortedData.findIndex((record, index) => getRowKey(record, index) === target);
    }, [selectedRowKey, sortedData, rowKeyDep]);

    // Tabbing in should land on the highlighted row. No focusOriginRef, so focus is not stolen.
    useEffect(() => {
        if (selectedIndex >= 0) {
            setFocusedRowIndex(selectedIndex);
        }
    }, [selectedIndex]);

    // Holds what was actually revealed, not the last key seen, so every not-ready path below
    // leaves it open to retry. Keyed on index too, since a sort moves a record without its key.
    const lastScrolledRef = useRef<{ key: React.Key; index: number } | undefined>(undefined);
    useEffect(() => {
        // Clearing releases the latch: re-selecting the same record must scroll again.
        if (selectedRowKey === undefined || selectedRowKey === null) {
            lastScrolledRef.current = undefined;
            return;
        }
        if (!scrollSelectedIntoView) {
            return;
        }
        if (selectedIndex < 0) {
            return;
        }
        const last = lastScrolledRef.current;
        if (last && last.key === selectedRowKey && last.index === selectedIndex) {
            return;
        }
        if (loading || error) {
            return;
        }
        // The scroll element arrives a commit later, so the virtualizers would drop this.
        if (isVirtual && !virtualScroller) {
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // The header height lands after this flush. Reveal anyway, but hold the latch open so
        // the measured re-run can place it properly.
        const scrollMarginPending = isStickyHeader && stickyHeaderHeight === 0;

        // Scoped by table id: a `column.render` can nest a whole Table inside a cell.
        const rowAt = (index: number) =>
            container.querySelector<HTMLElement>(
                `[data-table-id="${tableId}"][data-row-index="${index}"]`,
            );

        // `block: 'nearest'` no-ops when already visible, so a click never moves the viewport.
        // One element per change — interaction-time, not the mount-path read ENG-1166 banned.
        const reveal = (row: HTMLElement): void => {
            // scrollIntoView resolves `inline` too, and under `scrollX` a row is wider than the
            // port, so `nearest` would scroll 1px and hide the card's border.
            const body = bodyScrollRef.current;
            const outer = virtualScrollElement;
            const bodyLeft = body?.scrollLeft;
            const outerLeft = outer?.scrollLeft;
            row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            if (body && bodyLeft !== undefined) {
                body.scrollLeft = bodyLeft;
            }
            if (outer && outerLeft !== undefined) {
                outer.scrollLeft = outerLeft;
            }
            if (!scrollMarginPending) {
                lastScrolledRef.current = { key: selectedRowKey, index: selectedIndex };
            }
        };

        const mounted = rowAt(selectedIndex);
        if (mounted) {
            reveal(mounted);
            return;
        }

        // Not mounted: under virtualisation, ask the owning card's virtualizer for it.
        if (!isVirtual) {
            return;
        }
        const card = [...virtualCardsRef.current.values()].find(
            (entry) =>
                selectedIndex >= entry.startIndex && selectedIndex < entry.startIndex + entry.count,
        );
        if (!card) {
            return;
        }
        card.scrollToIndex(selectedIndex - card.startIndex);

        // scrollToIndex positions from estimated heights, so correct once the real row measures.
        let frame = 0;
        let attempts = 0;
        const correct = (): void => {
            const row = rowAt(selectedIndex);
            if (row) {
                reveal(row);
                return;
            }
            if (++attempts < VIRTUAL_SCROLL_CORRECTION_FRAMES) {
                frame = requestAnimationFrame(correct);
            }
        };
        frame = requestAnimationFrame(correct);
        return () => cancelAnimationFrame(frame);
    }, [
        selectedRowKey,
        selectedIndex,
        isVirtual,
        virtualScroller,
        virtualScrollElement,
        isStickyHeader,
        stickyHeaderHeight,
        loading,
        error,
        tableId,
        scrollSelectedIntoView,
    ]);

    // Set only for user-driven moves, so the effect below doesn't steal focus on an
    // unrelated re-render.
    const focusOriginRef = useRef<HTMLElement | null>(null);

    const moveRowFocus = (from: HTMLElement, toIndex: number): void => {
        focusOriginRef.current = from;
        setFocusedRowIndex(Math.min(Math.max(0, toIndex), sortedData.length - 1));
    };

    // Focus after the commit, not in a rAF: the new row only becomes the tabbable one
    // once React has re-rendered. Scoped by table id so sibling tables can't steal it.
    useLayoutEffect(() => {
        const origin = focusOriginRef.current;
        if (!origin) {
            return;
        }
        focusOriginRef.current = null;
        const root = origin.getRootNode() as ParentNode;
        const rowAt = (index: number) =>
            root.querySelector<HTMLElement>(
                `[data-table-id="${tableId}"][data-row-index="${index}"]`,
            );

        const target = rowAt(rovingIndex);
        if (target) {
            target.focus();
            return;
        }

        // Target unmounted (virtualised, or windowed data). Land on the nearest rendered
        // row — otherwise nothing is tabbable and the table drops out of the tab order.
        const rendered = root.querySelectorAll<HTMLElement>(
            `[data-table-id="${tableId}"][data-row-index]`,
        );
        const nearest = rovingIndex > 0 ? rendered[rendered.length - 1] : rendered[0];
        if (nearest) {
            setFocusedRowIndex(Number(nearest.dataset.rowIndex));
            nearest.focus();
        }
    }, [rovingIndex, tableId]);

    const rowKeyDown =
        (index: number, activate?: () => void) =>
        (event: React.KeyboardEvent<HTMLElement>): void => {
            const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
            if (step !== undefined) {
                event.preventDefault();
                moveRowFocus(event.currentTarget, index + step);
                return;
            }
            // Home/End only when every row is mounted; under virtualisation the target row does
            // not exist yet, so falling through lets the scroll container jump natively.
            if (!isVirtual && (event.key === 'Home' || event.key === 'End')) {
                event.preventDefault();
                moveRowFocus(event.currentTarget, event.key === 'Home' ? 0 : sortedData.length - 1);
                return;
            }
            // Enter only: neither row path claims button semantics, so Space scrolls.
            if (activate) {
                activateOnEnterOrSpace(event, activate, 'enter');
            }
        };

    if (headerVariant === 'detached') {
        const scrollMinWidth = scrollX
            ? computeScrollMinWidth(columns as TableColumns<unknown>)
            : 0;
        const rowMinWidthStyle: React.CSSProperties = scrollX ? { minWidth: scrollMinWidth } : {};

        // flexShrink 0: as a flex item of a height-bounded body, an overflow-hidden card would
        // shrink-to-fit and clip its rows instead of letting the body scroll.
        const sectionCardStyle: React.CSSProperties = {
            border: '1px solid var(--malachite-card-border, #e8e8e8)',
            borderRadius: SECTION_CARD_RADIUS,
            overflow: 'hidden',
            flexShrink: 0,
            ...rowMinWidthStyle,
        };

        const renderRow = (
            record: T,
            index: number,
            isLastInSection = false,
            isFirstInSection = false,
            virtualMeasure?: VirtualMeasureProps,
            // Virtual cards substitute the nearest mounted row; elsewhere all rows are mounted.
            tabStopIndex: number = rovingIndex,
        ): React.ReactElement => {
            const key = getRowKey(record, index);
            const rowActions = onRow?.(record, index) || {};
            const variant = rowVariant?.(record, index);
            const isSelected = index === selectedIndex;
            const handleClick =
                onClickRow || rowActions.onClick
                    ? () => {
                          onClickRow?.(record, index);
                          rowActions.onClick?.(record, index);
                      }
                    : undefined;
            const isClickable = Boolean(handleClick);
            const rowBorder = isLastInSection ? {} : ROW_BORDER_STYLE;
            return (
                <div
                    key={key}
                    ref={virtualMeasure?.measureRef}
                    data-index={virtualMeasure?.index}
                    data-table-id={tableId}
                    data-row-index={index}
                    // No `role="button"`: it takes presentational children, flattening the cells
                    // and stripping a nested ActionMenu from the a11y tree.
                    aria-current={isSelected ? 'true' : undefined}
                    tabIndex={isClickable ? (index === tabStopIndex ? 0 : -1) : undefined}
                    onKeyDown={isClickable ? rowKeyDown(index, handleClick) : undefined}
                    className={`
                        group bg-[var(--malachite-table-row-background,#fff)] flex min-h-10 items-stretch relative w-full
                        ${isClickable ? `cursor-pointer hover:bg-[var(--malachite-table-row-hover-background,#fafafa)] ${FOCUS_RING_INSET}` : ''}
                        ${rowClassName?.(record, index) || ''}
                    `}
                    style={{
                        ...rowBorder,
                        ...rowMinWidthStyle,
                        scrollMarginTop: rowScrollMarginTop,
                        // Echo the card's clip, or it cuts the accent bar's end into a stub.
                        ...(isFirstInSection
                            ? {
                                  borderTopLeftRadius: SECTION_CARD_RADIUS,
                                  borderTopRightRadius: SECTION_CARD_RADIUS,
                              }
                            : {}),
                        ...(isLastInSection
                            ? {
                                  borderBottomLeftRadius: SECTION_CARD_RADIUS,
                                  borderBottomRightRadius: SECTION_CARD_RADIUS,
                              }
                            : {}),
                        // Set unconditionally it would beat `hover:bg-*` on every row.
                        ...(isSelected || variant
                            ? {
                                  backgroundColor: variant
                                      ? `var(--malachite-table-row-variant-${variant}-background)`
                                      : 'var(--malachite-table-row-background, #fff)',
                              }
                            : {}),
                        ...(isSelected
                            ? {
                                  // backgroundImage: as a colour the tint would composite against
                                  // the card behind the row.
                                  backgroundImage: `linear-gradient(${SELECTED_ROW_BACKGROUND}, ${SELECTED_ROW_BACKGROUND})`,
                                  // Inset box-shadow, not `ring-*` — see the classic cell.
                                  boxShadow: `inset 2px 0 0 ${SELECTED_ROW_ACCENT}`,
                              }
                            : {}),
                    }}
                    onClick={
                        handleClick
                            ? (event) => {
                                  if (!isFromInteractiveDescendant(event)) {
                                      handleClick();
                                  }
                              }
                            : undefined
                    }
                    onDoubleClick={
                        rowActions.onDoubleClick
                            ? () => rowActions.onDoubleClick?.(record, index)
                            : undefined
                    }
                    onContextMenu={
                        rowActions.onContextMenu
                            ? () => rowActions.onContextMenu?.(record, index)
                            : undefined
                    }
                    onMouseEnter={
                        rowActions.onMouseEnter
                            ? () => rowActions.onMouseEnter?.(record, index)
                            : undefined
                    }
                    onMouseLeave={
                        rowActions.onMouseLeave
                            ? () => rowActions.onMouseLeave?.(record, index)
                            : undefined
                    }
                >
                    {columns.map((column) => (
                        <DetachedCell<T>
                            key={column.key}
                            column={column}
                            record={record}
                            paddingYClass={bodyCellPaddingY}
                            index={index}
                            size={size}
                            isSelected={isSelected}
                        />
                    ))}
                </div>
            );
        };

        const renderSkeletonRow = (index: number, isLast = false): React.ReactElement => (
            <div
                key={`skeleton-${index}`}
                className="bg-white flex min-h-10 items-stretch w-full"
                // Last row: no bottom border; the card border closes the section.
                style={{ ...(isLast ? {} : ROW_BORDER_STYLE), ...rowMinWidthStyle }}
            >
                {columns.map((column) => {
                    // Mirror DetachedCell's widths and placement so skeleton columns don't jump
                    // on load.
                    const titleIsEmpty = column.title === '' || column.title == null;
                    const hasFixedWidth = titleIsEmpty && column.width !== undefined;
                    if (hasFixedWidth) {
                        return (
                            <div
                                key={column.key}
                                className={`flex items-center ${fixedSlotJustifyClass(column.align)} shrink-0 overflow-clip px-4 ${bodyCellPaddingY}`}
                                style={fixedSlotStyle(column as TableColumnDefinition<unknown>)}
                            >
                                <div className="h-3 bg-gray-200 rounded animate-pulse w-2/5"></div>
                            </div>
                        );
                    }
                    return (
                        <div
                            key={column.key}
                            className={`flex flex-[1_0_0] items-center ${alignJustifyClass(column.align)} min-w-0 overflow-clip px-4 ${bodyCellPaddingY}`}
                            style={cellWidthStyle(column as TableColumnDefinition<unknown>)}
                        >
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-3/5"></div>
                        </div>
                    );
                })}
            </div>
        );

        const headerRow = showHeader ? (
            <div className="flex min-h-10 items-stretch w-full" style={rowMinWidthStyle}>
                {columns.map((column) => (
                    <DetachedHeaderCell<T>
                        key={column.key}
                        column={column}
                        sortDirection={sortConfig?.key === column.key ? sortConfig.direction : null}
                        onSort={column.sorter ? () => handleSort(column.key) : undefined}
                    />
                ))}
            </div>
        ) : null;

        return (
            <div
                ref={containerRef}
                className={`w-full ${className}`}
                style={{ ...FOREGROUND_RESET, ...style }}
            >
                <div
                    className={`bg-[var(--malachite-table-background,#f7f7f7)] flex flex-col items-start ${isStickyHeader ? '' : 'overflow-clip'} p-[4px] relative rounded-xl w-full`}
                >
                    {showHeader && (
                        <div
                            ref={headerScrollRef}
                            onScroll={
                                scrollX
                                    ? () =>
                                          syncScroll(headerScrollRef.current, bodyScrollRef.current)
                                    : undefined
                            }
                            style={{
                                ...(isStickyHeader
                                    ? {
                                          position: 'sticky',
                                          top: stickyHeaderTop,
                                          zIndex: 8,
                                          backgroundColor:
                                              'var(--malachite-table-header-background, #f7f7f7)',
                                          marginInline: -DETACHED_CARD_PADDING,
                                          width: `calc(100% + ${DETACHED_CARD_PADDING * 2}px)`,
                                          paddingInline: DETACHED_CARD_PADDING,
                                          // Vertically too, or the card's top frame sits above
                                          // the docked header and rows scroll through it.
                                          marginTop: -DETACHED_CARD_PADDING,
                                          paddingTop: DETACHED_CARD_PADDING,
                                      }
                                    : { width: '100%' }),
                                ...(scrollX ? { overflowX: 'auto', scrollbarWidth: 'none' } : {}),
                            }}
                        >
                            {isStickyHeader && <StickyHeaderCornerCap />}
                            {headerRow}
                        </div>
                    )}

                    <div
                        ref={bodyScrollRefCallback}
                        onScroll={
                            scrollX
                                ? () => syncScroll(bodyScrollRef.current, headerScrollRef.current)
                                : undefined
                        }
                        className={`flex flex-col items-start ${scrollX ? 'overflow-x-auto' : 'overflow-x-clip'} ${bodyOwnsScroll ? 'overflow-y-auto' : 'overflow-y-visible'} w-full`}
                        style={{
                            // Firefox + modern Chromium only; older Webkit keeps its default.
                            scrollbarWidth: 'thin',
                            scrollbarColor:
                                'var(--malachite-text-placeholder, rgb(187, 187, 187)) transparent',
                            // With an external virtual scroller the body is overflow-visible and
                            // a bounded box would overlap content below it.
                            ...(scrollHeight && bodyOwnsScroll
                                ? {
                                      maxHeight: computeScrollableMaxHeight(
                                          scrollHeight,
                                          !!pagination,
                                      ),
                                  }
                                : {}),
                        }}
                    >
                        {error ? (
                            <div className="flex flex-col w-full" style={sectionCardStyle}>
                                <div className="flex flex-col items-center justify-center bg-white w-full py-12">
                                    <div className="text-[var(--malachite-text-secondary)] text-[14px] font-medium mb-1">
                                        {errorTitle}
                                    </div>
                                    <div className="text-[var(--malachite-text-secondary)] text-[13px]">
                                        {errorSubTitle}
                                    </div>
                                </div>
                            </div>
                        ) : loading ? (
                            <div className="flex flex-col w-full" style={sectionCardStyle}>
                                {(() => {
                                    const count = pagination?.pageSize
                                        ? Math.min(pagination.pageSize, 10)
                                        : 5;
                                    return Array.from({ length: count }).map((_, i) =>
                                        renderSkeletonRow(i, i === count - 1),
                                    );
                                })()}
                            </div>
                        ) : sortedData.length === 0 ? (
                            <div className="flex flex-col w-full" style={sectionCardStyle}>
                                <div className="flex flex-col items-center justify-center bg-white w-full py-12">
                                    <div className="text-[var(--malachite-text-secondary)] text-[14px] font-medium">
                                        {emptyTitle}
                                    </div>
                                    {emptySubTitle && (
                                        <div className="text-[var(--malachite-text-secondary)] text-[13px] mt-1">
                                            {emptySubTitle}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            (() => {
                                const renderCard = (
                                    records: T[],
                                    startIndex: number,
                                    cardKey: string,
                                ): React.ReactElement => {
                                    if (isVirtual) {
                                        return (
                                            <VirtualSectionCard<T>
                                                key={`section-card-${cardKey}`}
                                                cardKey={cardKey}
                                                records={records}
                                                startIndex={startIndex}
                                                scrollElement={virtualScroller}
                                                estimateRowHeight={virtualEstimate}
                                                style={sectionCardStyle}
                                                renderRow={renderRow}
                                                registerHandle={registerVirtualCard}
                                                rovingIndex={rovingIndex}
                                            />
                                        );
                                    }
                                    return (
                                        <div
                                            key={`section-card-${cardKey}`}
                                            className="flex flex-col w-full"
                                            style={sectionCardStyle}
                                        >
                                            {records.map((record, i) =>
                                                renderRow(
                                                    record,
                                                    startIndex + i,
                                                    i === records.length - 1,
                                                    i === 0,
                                                ),
                                            )}
                                        </div>
                                    );
                                };

                                if (sectionDividers.size === 0) {
                                    return renderCard(sortedData, 0, 'all');
                                }

                                const starts = [...sectionDividers.keys()].sort((a, b) => a - b);
                                return starts.map((start, i) => {
                                    const section = sectionDividers.get(start);
                                    if (!section) {
                                        return null;
                                    }
                                    const end =
                                        i + 1 < starts.length ? starts[i + 1] : sortedData.length;
                                    const records = sortedData.slice(start, end);
                                    const dividerContent = renderSectionDivider?.(
                                        section.key,
                                        section.records,
                                    );
                                    return (
                                        <React.Fragment key={`section-${section.key}`}>
                                            {dividerContent != null ? (
                                                <div
                                                    className="table-section-divider"
                                                    style={rowMinWidthStyle}
                                                >
                                                    {dividerContent}
                                                </div>
                                            ) : null}
                                            {renderCard(records, start, section.key)}
                                        </React.Fragment>
                                    );
                                });
                            })()
                        )}
                    </div>
                </div>

                {pagination && !loading && (
                    <TablePagination
                        current={pagination.current ?? 1}
                        pageSize={pagination.pageSize ?? 10}
                        total={pagination.total ?? 0}
                        showSizeChanger={pagination.showSizeChanger}
                        showQuickJumper={pagination.showQuickJumper}
                        showTotal={pagination.showTotal}
                        pageSizeOptions={pagination.pageSizeOptions}
                        onChange={pagination.onChange}
                        onShowSizeChange={pagination.onShowSizeChange}
                    />
                )}
            </div>
        );
    }

    const renderHeader = (column: TableColumnDefinition<T>) => {
        const isSortable = column.sorter;
        const isActive = sortConfig?.key === column.key;
        const sortDirection = isActive ? sortConfig?.direction : null;

        const headerContent = (
            <div className={`flex items-center gap-2 ${alignJustifyClass(column.align)}`}>
                <span>{column.title}</span>
                {column.info && <HeaderInfo info={column.info} />}
                {isSortable && (
                    <div className="flex flex-col">
                        <CaretUpIcon
                            size={12}
                            className={sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}
                        />
                        <CaretDownIcon
                            size={12}
                            className={`-mt-1 ${sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`}
                        />
                    </div>
                )}
            </div>
        );

        return (
            <th
                key={column.key}
                className={`
                    table-header table-cell px-4 py-3 font-semibold border-0 first:rounded-tl-lg last:rounded-tr-lg
                    ${getSizeClasses(size)}
                    ${alignTextClass(column.align)}
                `}
                style={{ width: column.width }}
                aria-sort={isSortable ? ariaSortValue(sortDirection) : undefined}
            >
                {isSortable ? (
                    <button
                        {...sortButtonProps(column.title, () => handleSort(column.key))}
                        className={`flex w-full items-center ${alignJustifyClass(column.align)} cursor-pointer hover:opacity-80 appearance-none bg-transparent border-0 p-0 font-semibold text-inherit ${alignTextClass(column.align)}`}
                    >
                        {headerContent}
                    </button>
                ) : (
                    headerContent
                )}
            </th>
        );
    };

    if (error) {
        return (
            <div className="w-full" style={{ ...FOREGROUND_RESET, ...style }}>
                <div
                    className="overflow-x-auto"
                    style={{
                        ...(scrollHeight ? { maxHeight: scrollHeight } : {}),
                    }}
                >
                    <table className="w-full border-collapse rounded-lg overflow-hidden">
                        {showHeader && (
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            className={`
                                                    ${bordered ? 'table-header' : 'table-header'} table-cell px-4 py-3 font-semibold border-0 first:rounded-tl-lg last:rounded-tr-lg
                                                    ${bordered ? 'table-cell-bordered table-cell-bordered-top' : ''}
                                                    ${bordered && column === columns[0] ? 'table-cell-bordered-left' : ''}
                                                    ${bordered && column === columns[columns.length - 1] ? 'table-cell-bordered-right' : ''}
                                                    ${getSizeClasses(size)}
                                                    ${alignTextClass(column.align)}
                                                `}
                                            style={{ width: column.width }}
                                        >
                                            <div
                                                className={`flex items-center gap-2 ${alignJustifyClass(column.align)}`}
                                            >
                                                <span>{column.title}</span>
                                                {column.info && <HeaderInfo info={column.info} />}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        )}
                        <tbody>
                            <tr>
                                <td
                                    colSpan={columns.length + (rowSelection ? 1 : 0)}
                                    className="px-4 py-12 text-center"
                                >
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="table-error-icon w-12 h-12 rounded-full flex items-center justify-center mb-4">
                                            <WarningTriangleIcon size={24} />
                                        </div>
                                        <div className="table-error-title text-lg font-semibold mb-2">
                                            {errorTitle}
                                        </div>
                                        <div className="table-error-subtitle text-sm">
                                            {errorSubTitle}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`w-full ${className}`}
            style={{ ...FOREGROUND_RESET, ...style }}
        >
            <div
                className={`overflow-hidden rounded-lg ${bordered ? 'table-bordered-container' : 'table-container-border'}`}
                data-bordered={bordered ? 'true' : 'false'}
            >
                <div
                    className="overflow-x-auto"
                    style={{
                        ...(scrollHeight ? { maxHeight: scrollHeight } : {}),
                    }}
                >
                    <table
                        className={`w-full ${layout === 'fixed' || columns.some((col) => col.width) ? 'table-fixed' : 'table-auto'} ${bordered ? 'border-separate table-bordered' : 'border-collapse'} rounded-lg overflow-hidden`}
                        style={{ borderSpacing: bordered ? '0' : '0' }}
                    >
                        {showHeader && (
                            <thead>
                                <tr>{columns.map((column) => renderHeader(column))}</tr>
                            </thead>
                        )}
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <tr
                                        key={index}
                                        className={`table-row ${bordered ? 'table-row-bordered' : ''}`}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={`table-cell px-4 ${bodyCellPaddingY} text-left ${getSizeClasses(size)}`}
                                                style={{ width: column.width }}
                                            >
                                                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : sortedData.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length + (rowSelection ? 1 : 0)}
                                        className="px-4 py-12 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="text-[var(--malachite-text-secondary)] text-[14px] font-medium">
                                                {emptyTitle}
                                            </div>
                                            {emptySubTitle && (
                                                <div className="text-[var(--malachite-text-secondary)] text-[13px] mt-1">
                                                    {emptySubTitle}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                sortedData.map((record, index) => {
                                    const key = getRowKey(record, index);
                                    const rowActions = onRow?.(record, index) || {};
                                    const variant = rowVariant?.(record, index);

                                    const isFirstRow = index === 0;
                                    const isLastRow = index === data.length - 1;

                                    const isSelected = index === selectedIndex;
                                    const variantBackground = variant
                                        ? `var(--malachite-table-row-variant-${variant}-background)`
                                        : undefined;

                                    const handleRowClick =
                                        onClickRow || rowActions.onClick
                                            ? () => {
                                                  onClickRow?.(record, index);
                                                  rowActions.onClick?.(record, index);
                                              }
                                            : undefined;

                                    return (
                                        // Keeps `row` semantics: `role="button"` would strip the table
                                        // structure from screen readers.
                                        <tr
                                            key={key}
                                            data-table-id={tableId}
                                            data-row-index={index}
                                            // `aria-selected` needs a role this row lacks.
                                            aria-current={isSelected ? 'true' : undefined}
                                            tabIndex={
                                                handleRowClick
                                                    ? index === rovingIndex
                                                        ? 0
                                                        : -1
                                                    : undefined
                                            }
                                            onKeyDown={
                                                handleRowClick
                                                    ? rowKeyDown(index, handleRowClick)
                                                    : undefined
                                            }
                                            // Drops hover:opacity-80: opacity is not a background,
                                            // so the cells can't beat it and the row fades below AA.
                                            className={`
                                                ${bordered ? 'table-row-bordered' : `table-row ${isSelected ? '' : 'hover:opacity-80'}`}
                                                ${rowClassName?.(record, index) || ''}
                                                ${handleRowClick ? `cursor-pointer ${FOCUS_RING_INSET}` : ''}
                                            `}
                                            onClick={
                                                handleRowClick
                                                    ? (event) => {
                                                          if (!isFromInteractiveDescendant(event)) {
                                                              handleRowClick();
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            onDoubleClick={
                                                rowActions.onDoubleClick
                                                    ? () =>
                                                          rowActions.onDoubleClick?.(record, index)
                                                    : undefined
                                            }
                                            onContextMenu={
                                                rowActions.onContextMenu
                                                    ? () =>
                                                          rowActions.onContextMenu?.(record, index)
                                                    : undefined
                                            }
                                            onMouseEnter={
                                                rowActions.onMouseEnter
                                                    ? () => rowActions.onMouseEnter?.(record, index)
                                                    : undefined
                                            }
                                            onMouseLeave={
                                                rowActions.onMouseLeave
                                                    ? () => rowActions.onMouseLeave?.(record, index)
                                                    : undefined
                                            }
                                        >
                                            {columns.map((column) => {
                                                const isFirstColumn = column === columns[0];
                                                const isLastColumn =
                                                    column === columns[columns.length - 1];

                                                return (
                                                    <TableCell<T>
                                                        key={column.key}
                                                        column={column}
                                                        record={record}
                                                        index={index}
                                                        size={size}
                                                        paddingYClass={bodyCellPaddingY}
                                                        bordered={bordered}
                                                        isFirstRow={isFirstRow}
                                                        isLastRow={isLastRow}
                                                        isFirstColumn={isFirstColumn}
                                                        isLastColumn={isLastColumn}
                                                        variantBackground={variantBackground}
                                                        isSelected={isSelected}
                                                        showSelectedAccent={
                                                            isSelected && isFirstColumn
                                                        }
                                                    />
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination && !loading && (
                <TablePagination
                    current={pagination.current ?? 1}
                    pageSize={pagination.pageSize ?? 10}
                    total={pagination.total ?? 0}
                    showSizeChanger={pagination.showSizeChanger}
                    showQuickJumper={pagination.showQuickJumper}
                    showTotal={pagination.showTotal}
                    pageSizeOptions={pagination.pageSizeOptions}
                    onChange={pagination.onChange}
                    onShowSizeChange={pagination.onShowSizeChange}
                />
            )}
        </div>
    );
};

export default Table;
